/**
 * 사이트맵 페이지 컴포넌트
 * 웹사이트의 전체 구조를 한눈에 볼 수 있도록 모든 메뉴와 서브메뉴를
 * 계층적으로 표시합니다. 사용자의 네비게이션을 돕고 SEO에 도움을 줍니다.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import SubPage from '../template/SubPage';
import { MAIN_MENU } from '../constant/menuData';
import MyHelmet from '../hooks/Helmet';

/**
 * SiteMap 컴포넌트
 * MAIN_MENU 데이터를 사용하여 모든 메뉴 구조를 동적으로 표시합니다.
 */
function SiteMap() {
  return (
    <>
      <MyHelmet />
      <SubPage
        title="사이트맵"
        description="중구행복돌봄센터는 함께하는 가치로 사람중심, 상호신뢰와 배려, 같이 삶을 실현하는 공동체입니다."
        menuItems={[]} // 사이트맵은 사이드바 메뉴 없음
        currentNo={null}
        submenuTitle="사이트맵"
        breadcrumbMenu="법인소개"
        breadcrumbLink="/introduce/1"
        telHeight="0"
      >
      {/* 사이트맵 메뉴 구조 */}
      <MenuContainer>
        <Hidden>메인메뉴</Hidden>
        <Navigation>
          <Depth1>
            {MAIN_MENU.map((menu) => (
              <Depth1Item key={menu.id}>
                <MenuLink to={menu.path}>{menu.label}</MenuLink>

                <Depth2>
                  {menu.subMenus.map((subMenu) => (
                    <li key={subMenu.id}>
                      <SubMenuLink to={subMenu.path}>
                        {subMenu.label}
                      </SubMenuLink>
                    </li>
                  ))}
                </Depth2>
              </Depth1Item>
            ))}
          </Depth1>
        </Navigation>
      </MenuContainer>
    </SubPage>
    </>
  );
}

export default SiteMap;

/* Styled Components */

const Hidden = styled.h2`
  position: absolute;
  overflow: hidden;
  top: 0;
  visibility: hidden;
  width: 1px;
  height: 1px;
`;

const MenuContainer = styled.div`
  /* 데스크톱 기본 스타일 */
  margin: 50px 0;

  ol,
  ol li,
  ul,
  ul li {
    list-style: none;
    margin-left: 0;
  }

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    margin: 30px 0;
    padding: 0 5%;
  }
`;

const Navigation = styled.nav`
  margin: 0 auto;
`;

const Depth1 = styled.ol`
  /* 데스크톱 기본 스타일 */
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  list-style: none;
  margin: 0;
  padding: 0;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    flex-direction: column;
    gap: 0;
  }
`;

const Depth1Item = styled.li`
  /* 데스크톱 기본 스타일 */
  flex: 0 0 calc(25% - 15px);
  box-sizing: border-box;
  border-left: 1px solid #ffffff;
  border-right: 1px solid #ffffff;
  min-width: 180px;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    flex: 0 0 100%;
    width: 100%;
    min-width: 100%;
    margin-bottom: 20px;
    border-left: none;
    border-right: none;
  }
`;

const MenuLink = styled(Link)`
  /* 데스크톱 기본 스타일 */
  font-size: 17px;
  font-family: 'koPubWorld DotumB';
  display: block;
  border-bottom: 1px solid #e5857c;
  border-top: 1px solid #e5857c;
  width: 100%;
  height: 40px;
  line-height: 38px;
  box-sizing: border-box;
  text-decoration: none;
  margin-bottom: 10px;
  background-color: #f4f4f4;
  padding-left: 20px;
  color: #e5857c;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    font-size: 16px;
    height: 45px;
    line-height: 43px;
    padding-left: 15px;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    font-size: 18px;
    height: 50px;
    line-height: 48px;
  }
`;

const Depth2 = styled.ol`
  /* 데스크톱 기본 스타일 */
  font-size: 18px;
  display: block;
  min-height: 200px;
  background-color: #ffffff;
  color: #666666;
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    float: none;
    border: 0;
    background-color: #ffffff;
  }

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    min-height: auto;
    border: 1px solid #f0f0f0;
    margin-bottom: 10px;
  }
`;

const SubMenuLink = styled(Link)`
  && {
    /* 데스크톱 기본 스타일 */
    font-size: 15px;
    font-family: 'koPubWorld DotumM';
    text-decoration: none;
    font-weight: 0;
    text-align: left;
    color: #666666;
    border-bottom: none;
    border-top: none;
    height: 32px;
    line-height: 30px;
    padding-left: 20px;
    display: block;

    /* 모바일 스타일 */
    @media (max-width: 1023px) {
      font-size: 14px;
      height: 40px;
      line-height: 38px;
      padding-left: 15px;
    }

    @media (min-width: 768px) and (max-width: 1023px) {
      font-size: 16px;
      height: 45px;
      line-height: 43px;
    }
  }

  &&:hover {
    color: #e5857c;
    text-decoration: none;
    background-color: rgba(255, 255, 255, 0.5);
  }
`;
